/**
 * Unified Inbox — merges chat + Discord (+ email, once backend lands)
 * into a single thread feed, sorted by most-recent activity.
 *
 * Backend doesn't expose an aggregator yet; we fan out to the three
 * source endpoints and merge client-side. Reply-routing is per-source
 * (chat goes to the chat endpoint, Discord goes to crm/messages/send,
 * email will go to a new admin_service endpoint).
 */

import { buildQuery, request } from './client'
import { listChatSessions, type ChatSession } from './chat'

export type InboxSource = 'chat' | 'discord' | 'email'
export type InboxStatus = 'open' | 'closed' | 'missed' | 'snoozed'

/**
 * Identity realm a chat visitor authenticated against.
 *
 * `console`   — an AllenHark Console user (`console_user_id` is set).
 * `getshreds` — a getshreds.xyz user (`getshreds_user_id` is set;
 *               `console_user_id` stays null — it is NOT namespaced).
 *
 * The wire field is optional: every thread written before the realm
 * existed carries none. See `normalizeRealm` — a missing realm is
 * always "console", never "unknown" and never hidden.
 */
export type InboxRealm = 'console' | 'getshreds'

/**
 * The realm-defaulting rule, in one place.
 *
 * A MISSING realm must be read as "console". Existing chat_sessions
 * rows have no such field, and treating them as anything else would
 * silently reclassify (or, with a realm filter applied, hide) every
 * thread the inbox has ever had.
 *
 * Anything that is not exactly "getshreds" also falls back to
 * "console" so an unknown realm string from a newer backend degrades
 * to today's behaviour instead of producing a third, unhandled state.
 */
export function normalizeRealm(realm?: string | null): InboxRealm {
  return realm === 'getshreds' ? 'getshreds' : 'console'
}

export interface InboxThread {
  /** Source-qualified id so chat:abc vs discord:abc don't collide. */
  key: string
  id: string
  source: InboxSource
  subject?: string | null
  /** Display name of the other party. Name > email > 'Anonymous visitor'. */
  participant: string
  /** Separate name field — lets the UI show name + email as two lines. */
  name?: string | null
  /** Best-known email for the participant (drives avatar + CRM link). */
  email?: string | null
  /** Linked CRM user id when known. Null for getshreds visitors. */
  console_user_id?: string | null
  /**
   * Which product's identity system the visitor signed in with.
   * Optional on the wire; read it through `normalizeRealm`, never
   * directly, so a missing value defaults to 'console'.
   */
  realm?: InboxRealm | string | null
  /** GetShreds user id — present only when realm === 'getshreds'. */
  getshreds_user_id?: string | null
  discord_user_id?: string | null
  discord_username?: string | null
  status: InboxStatus | string
  last_message_at: string
  created_at: string
  preview?: string | null
  unread_count?: number
  country?: string | null
  city?: string | null
  ip?: string | null
  /** Last page the visitor was on (chat source only). */
  last_page_url?: string | null
  /** Hostname of the site the widget was served on. */
  host?: string | null
  /** Lead-qualification tag, chat source only for now. */
  qualification?:
    | 'lead'
    | 'qualified'
    | 'customer'
    | 'spam'
    | 'unqualified'
    | string
    | null
  /** For email threads: the address the email was sent TO. */
  recipient_address?: string | null
  /** Admin user ID who owns this thread (assigned agent for chat,
   *  custom_email owner for email). Null = shared/unassigned. */
  owner_id?: string | null

  /* Inbox-triage fields (Relay feat/chat-inbox-backend). All optional:
   * older deployments send none of them, and the UI must then fall back
   * to its local behaviour. `tags` being an array is the tell-tale that
   * the thread came from the newer backend. */
  /** "visitor" | "agent" | "system" (chat), "inbound" | "outbound". */
  last_direction?: string | null
  /** When the visitor's oldest unanswered message arrived. */
  waiting_since?: string | null
  tags?: string[]
  priority?: boolean
  snoozed_until?: string | null
}

/* ──── Discord DMs ──── */

export interface DiscordMessage {
  id: string
  discord_user_id: string
  discord_username?: string | null
  console_user_id?: string | null
  direction: 'inbound' | 'outbound'
  body: string
  created_at: string
}

export async function listDiscordMessages(params: {
  limit?: number
} = {}): Promise<{ messages: DiscordMessage[] }> {
  return request<{ messages: DiscordMessage[] }>(
    `/api/crm/messages${buildQuery({ limit: params.limit ?? 200 })}`,
  )
}

export async function listDiscordMessagesForUser(
  discordUserId: string,
  limit = 200,
): Promise<{ messages: DiscordMessage[] }> {
  return request<{ messages: DiscordMessage[] }>(
    `/api/crm/messages/discord/${encodeURIComponent(discordUserId)}${buildQuery({
      limit,
    })}`,
  )
}

export async function sendDiscordMessage(payload: {
  discord_user_id: string
  body: string
}): Promise<DiscordMessage> {
  return request<DiscordMessage>('/api/crm/messages/send', {
    method: 'POST',
    body: payload,
  })
}

/* ──── Thread aggregation ──── */

function chatToThread(s: ChatSession): InboxThread {
  return {
    key: `chat:${s.id}`,
    id: s.id,
    source: 'chat',
    subject: null,
    participant: s.name || s.email || 'Anonymous visitor',
    email: s.email ?? null,
    console_user_id: s.console_user_id ?? null,
    realm: normalizeRealm(s.realm),
    getshreds_user_id: s.getshreds_user_id ?? null,
    status: s.status,
    last_message_at: s.last_message_at ?? s.created_at,
    created_at: s.created_at,
    preview: null,
    unread_count: s.unread_count ?? undefined,
    country: s.country ?? null,
    host: s.host ?? null,
  }
}

/** Group Discord messages by discord_user_id into conversation threads. */
function discordToThreads(msgs: DiscordMessage[]): InboxThread[] {
  const map = new Map<
    string,
    { latest: DiscordMessage; count: number; earliest: DiscordMessage }
  >()
  for (const m of msgs) {
    const existing = map.get(m.discord_user_id)
    if (!existing) {
      map.set(m.discord_user_id, { latest: m, count: 1, earliest: m })
    } else {
      existing.count++
      if (m.created_at > existing.latest.created_at) existing.latest = m
      if (m.created_at < existing.earliest.created_at) existing.earliest = m
    }
  }
  return [...map.entries()].map(([uid, v]) => ({
    key: `discord:${uid}`,
    id: uid,
    source: 'discord',
    subject: null,
    participant: v.latest.discord_username ?? `@${uid.slice(-6)}`,
    email: null,
    console_user_id: v.latest.console_user_id ?? null,
    discord_user_id: uid,
    discord_username: v.latest.discord_username ?? null,
    // Discord DMs are not tied to a product login at all, so they
    // stay in the default realm.
    realm: 'console',
    status: 'open',
    last_message_at: v.latest.created_at,
    created_at: v.earliest.created_at,
    preview: v.latest.body.slice(0, 120),
    unread_count:
      v.latest.direction === 'inbound' ? 1 : undefined,
  }))
}

export interface ListInboxParams {
  /** Restrict to a single source.  Defaults to 'all'. */
  source?: 'all' | InboxSource
  /** Only applies to chat (Discord/email are always open). */
  status?: InboxStatus
  /**
   * Server-side match against participant / email AND the full message body
   * of every thread. Previously header-only, which is why searching for a
   * phrase said mid-conversation found nothing.
   */
  search?: string
  /** Restrict to a single originating site (chat host). Chat source only. */
  host?: string
  /** Page size (default 100, max 500 server-side). */
  limit?: number
  /** Page offset into the merged, filtered, sorted result. */
  offset?: number
  /* Triage backend only; older servers ignore unknown params, so the
   * client re-applies realm / assignee locally either way. */
  realm?: InboxRealm
  /** 'me' | 'unassigned' | an admin id */
  assignee?: string
  tag?: string
  sort?: 'newest' | 'waiting' | 'priority'
}

/** Server-wide chat totals for the view badges (triage backend only). */
export interface InboxViewTotals {
  open: number
  mine: number
  unassigned: number
  snoozed: number
}

export interface InboxListResult {
  threads: InboxThread[]
  counts: Record<'all' | InboxSource, number>
  /** Present only when the server sends the view totals. */
  totals?: InboxViewTotals
  errors: Partial<Record<InboxSource, string>>
  /** Matching threads before the page was cut — drives "N of M". */
  total: number
  offset: number
  limit: number
  hasMore: boolean
}

interface AggregatorResp {
  threads: InboxThread[]
  counts: {
    all: number
    chat: number
    discord: number
    email: number
    open?: number
    mine?: number
    unassigned?: number
    snoozed?: number
  }
  errors?: Partial<Record<InboxSource, string>>
  total?: number
  offset?: number
  limit?: number
  has_more?: boolean
}

/**
 * Hit the admin_service aggregator at /api/inbox/threads. Backend
 * merges chat + Discord server-side; frontend just renders.
 *
 * Falls back to the client-side merger for deployments where the
 * aggregator isn't live yet (pre-9bc262e admin_service): on 404 we
 * fan out to the per-source endpoints and merge here. The fallback
 * goes away once every deployment is on the aggregator.
 */
export async function listInbox(
  params: ListInboxParams = {},
): Promise<InboxListResult> {
  try {
    const r = await request<AggregatorResp>(
      `/api/inbox/threads${buildQuery({
        source: params.source && params.source !== 'all' ? params.source : undefined,
        status: params.status,
        search: params.search,
        host: params.host,
        limit: params.limit ?? 100,
        offset: params.offset || undefined,
        realm: params.realm,
        assignee: params.assignee,
        tag: params.tag,
        sort: params.sort && params.sort !== 'newest' ? params.sort : undefined,
      })}`,
    )
    return {
      // Normalise on read so nothing downstream has to remember the
      // default. Threads from a backend that predates the realm field
      // come back with none and must read as 'console'.
      threads: (r.threads ?? []).map((t) => ({
        ...t,
        realm: normalizeRealm(t.realm),
      })),
      counts: {
        all: r.counts?.all ?? r.threads?.length ?? 0,
        chat: r.counts?.chat ?? 0,
        discord: r.counts?.discord ?? 0,
        email: r.counts?.email ?? 0,
      },
      totals:
        typeof r.counts?.mine === 'number'
          ? {
              open: r.counts.open ?? 0,
              mine: r.counts.mine,
              unassigned: r.counts.unassigned ?? 0,
              snoozed: r.counts.snoozed ?? 0,
            }
          : undefined,
      errors: r.errors ?? {},
      // An older aggregator sends neither `total` nor `has_more`. Fall back
      // to the page itself so the control degrades to "no next page"
      // instead of offering one that returns nothing.
      total: r.total ?? r.threads?.length ?? 0,
      offset: r.offset ?? params.offset ?? 0,
      limit: r.limit ?? params.limit ?? 100,
      hasMore: r.has_more ?? false,
    }
  } catch (err) {
    // Only fall back on 404 (aggregator not deployed yet).  Any other
    // error surfaces as-is so outages aren't papered over.
    const status = (err as { status?: number } | null)?.status
    if (status !== 404) throw err
    return listInboxFallback(params)
  }
}

async function listInboxFallback(
  params: ListInboxParams,
): Promise<InboxListResult> {
  const limit = params.limit ?? 100
  // A host filter is chat-only — Discord has no originating site, so skip it.
  const [chatR, discordR] = await Promise.allSettled([
    listChatSessions({ limit, status: params.status, host: params.host }),
    params.host ? Promise.resolve({ messages: [] }) : listDiscordMessages({ limit }),
  ])

  const threads: InboxThread[] = []
  const errors: Partial<Record<InboxSource, string>> = {}

  if (chatR.status === 'fulfilled') {
    threads.push(...(chatR.value.sessions ?? []).map(chatToThread))
  } else {
    errors.chat =
      chatR.reason instanceof Error
        ? chatR.reason.message
        : String(chatR.reason)
  }
  if (discordR.status === 'fulfilled') {
    threads.push(...discordToThreads(discordR.value.messages ?? []))
  } else {
    errors.discord =
      discordR.reason instanceof Error
        ? discordR.reason.message
        : String(discordR.reason)
  }
  threads.sort((a, b) =>
    (b.last_message_at ?? '').localeCompare(a.last_message_at ?? ''),
  )

  const counts = {
    all: threads.length,
    chat: threads.filter((t) => t.source === 'chat').length,
    discord: threads.filter((t) => t.source === 'discord').length,
    email: 0,
  }
  // The pre-aggregator fallback has no server-side paging. Report the page
  // it produced and no next page, rather than implying one exists.
  return {
    threads,
    counts,
    errors,
    total: threads.length,
    offset: 0,
    limit,
    hasMore: false,
  }
}

/* ──── Email source ──── */

export interface SupportEmail {
  id: string
  thread_id: string
  direction: 'inbound' | 'outbound'
  from_address: string
  from_name?: string | null
  recipient: string
  subject?: string | null
  body_plain: string
  body_html?: string | null
  message_id?: string | null
  in_reply_to?: string | null
  received_at: string
  status?: string | null
  read?: boolean
}

export async function listEmailThread(
  threadId: string,
): Promise<{ messages: SupportEmail[] }> {
  const r = await request<{ emails: SupportEmail[] }>(
    `/api/inbox/email${buildQuery({ thread_id: threadId })}`,
  )
  return { messages: (r.emails ?? []).sort((a, b) => a.received_at.localeCompare(b.received_at)) }
}

/** Unified reply dispatcher — chat:id / discord:id / email:id.
 *  Chat / Discord only accept plain text. Email callers can pass
 *  a rendered HTML string from the WYSIWYG composer; the backend
 *  sends both parts to Mailgun so clients pick what they support. */
export async function replyToThread(
  threadKey: string,
  body: string,
  bodyHtml?: string,
): Promise<{ ok: boolean; id?: string }> {
  const payload: Record<string, unknown> = { body }
  if (bodyHtml && bodyHtml.trim()) payload.body_html = bodyHtml
  return request<{ ok: boolean; id?: string }>(
    `/api/inbox/threads/${encodeURIComponent(threadKey)}/reply`,
    { method: 'POST', body: payload },
  )
}

/* ──── Unified message shape ──── */

export interface InboxMessage {
  id: string
  direction: 'inbound' | 'outbound'
  body: string
  author?: string | null
  created_at: string
}
